import moment from 'moment-timezone';

export const NPS_AUDIENCES = [
  {
    value: 'all', label: 'Todos os perfis', meta: 'todos os perfis', roles: ['doctor', 'secretary'],
  },
  {
    value: 'doctors', label: 'Profissionais de saúde', meta: 'profissionais de saúde', roles: ['doctor'],
  },
  {
    value: 'secretaries', label: 'Secretarias', meta: 'secretarias', roles: ['secretary'],
  },
];

export const NPS_REMINDER_OPTIONS = [
  { value: 1, label: '1 dia' },
  { value: 3, label: '3 dias' },
  { value: 7, label: '7 dias' },
];

export const NPS_DEFAULT_QUESTION = 'Em uma escala de 0 a 10, quanto você recomendaria o Meagenda para outro profissional da área?';

// Degradê vermelho → verde, índice = nota.
export const NPS_SCALE_COLORS = [
  '#c0272d', '#d93025', '#e2582a', '#ea7127', '#ef8e26', '#f6c231',
  '#e8d417', '#d6cf16', '#bdc518', '#9ac545', '#7ab829',
];

export const NPS_ROLES = {
  doctor: { label: 'Médico', plural: 'Médicos' },
  secretary: { label: 'Secretária', plural: 'Secretarias' },
};

export const NPS_BANDS = {
  promoter: {
    label: 'Promotor', plural: 'Promotores', color: '#7ab829', text: '#4a7c1a', background: '#f0f9e8', border: '#b7e08a',
  },
  neutral: {
    label: 'Neutro', plural: 'Neutros', color: '#f6c231', text: '#8a6d00', background: '#fffbe6', border: '#ffe58f',
  },
  detractor: {
    label: 'Detrator', plural: 'Detratores', color: '#d93025', text: '#a8151a', background: '#fff1f0', border: '#ffa39e',
  },
};

export const NPS_BAND_ORDER = ['promoter', 'neutral', 'detractor'];

export const NPS_TONE_COLORS = {
  good: '#4a7c1a',
  mid: '#8a6d00',
  bad: '#a8151a',
  empty: '#8c8c8c',
};

export const getScoreBand = (score) => {
  if (score >= 9) {
    return 'promoter';
  }
  if (score >= 7) {
    return 'neutral';
  }
  return 'detractor';
};

// Branco sobre laranja/amarelo/verde não passa 4.5:1; só 0 e 1 levam texto branco.
export const getScoreTextColor = (score) => (score <= 1 ? '#fff' : 'rgba(0, 0, 0, 0.85)');

// Tom da média (0 a 10) segue as faixas: 9+ promotor, 7 a 8,9 neutro, abaixo detrator.
export const getScoreTone = (average) => {
  if (average === null || average === undefined) {
    return 'empty';
  }
  if (average >= 9) {
    return 'good';
  }
  if (average >= 7) {
    return 'mid';
  }
  return 'bad';
};

export const formatScore = (average) => (
  average === null || average === undefined ? '—' : average.toFixed(1).replace('.', ',')
);

// Tom do NPS clássico (−100 a 100): 50+ verde, 0 a 49 âmbar, negativo vermelho.
export const getNpsTone = (nps) => {
  if (nps === null || nps === undefined) {
    return 'empty';
  }
  if (nps >= 50) {
    return 'good';
  }
  if (nps >= 0) {
    return 'mid';
  }
  return 'bad';
};

export const formatNps = (nps) => (nps === null || nps === undefined ? '—' : String(nps));

// Média das notas, NPS clássico (% promotores − % detratores) e contagem por faixa.
// Campanha sem respostas tem média e NPS null.
export const summarizeScores = (responses) => {
  const counts = { promoter: 0, neutral: 0, detractor: 0 };
  let sum = 0;
  responses.forEach((response) => {
    counts[getScoreBand(response.score)] += 1;
    sum += response.score;
  });
  const total = responses.length;
  return {
    ...counts,
    total,
    average: total ? sum / total : null,
    nps: total
      ? Math.round((counts.promoter / total) * 100 - (counts.detractor / total) * 100)
      : null,
  };
};

export const getAudience = (value) => NPS_AUDIENCES.find((audience) => audience.value === value)
  || NPS_AUDIENCES[0];

export const isRoleInAudience = (role, audience) => getAudience(audience).roles.includes(role);

// Papel do usuário logado: profissional de saúde (conta principal ou usuário
// secundário com healthProfessional) ou secretária (todo o resto).
export const resolveNpsRole = ({ Auth, Profile }) => {
  if (!Auth.mainUser) {
    return 'doctor';
  }
  const customUserProfile = Profile.customUserProfile || {};
  return customUserProfile.healthProfessional ? 'doctor' : 'secretary';
};

export const getToday = () => moment().format('YYYY-MM-DD');

export const isCampaignActive = (campaign) => campaign.status === 'active' && campaign.endDate >= getToday();

export const formatPeriod = (startDate, endDate) => (
  `${moment(startDate, 'YYYY-MM-DD').format('DD/MM')} – ${moment(endDate, 'YYYY-MM-DD').format('DD/MM')}`
);

export const daysUntil = (date) => moment(date, 'YYYY-MM-DD').startOf('day').diff(moment().startOf('day'), 'days');

export const formatDaysLeft = (endDate) => {
  const days = daysUntil(endDate);
  if (days <= 0) {
    return 'encerra hoje';
  }
  if (days === 1) {
    return 'encerra amanhã';
  }
  return `encerra em ${days} dias`;
};

// Junta a campanha com suas respostas e dispensas e calcula tudo que as telas mostram.
export const buildCampaignStats = (id, campaign, responsesByUid = {}, dismissalsByUid = {}) => {
  const responses = Object.keys(responsesByUid)
    .map((uid) => ({
      id: uid,
      uid,
      ...responsesByUid[uid],
      band: getScoreBand(responsesByUid[uid].score),
    }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const respondedUids = new Set(responses.map((response) => response.uid));
  const dismissedCount = Object.keys(dismissalsByUid)
    .filter((uid) => !respondedUids.has(uid))
    .length;
  const summary = summarizeScores(responses);
  const byRole = Object.keys(NPS_ROLES)
    .map((role) => {
      const roleResponses = responses.filter((response) => response.role === role);
      const roleSummary = summarizeScores(roleResponses);
      return {
        role,
        label: NPS_ROLES[role].plural,
        count: roleResponses.length,
        average: roleSummary.average,
        nps: roleSummary.nps,
      };
    })
    .filter((role) => role.count > 0);
  return {
    id,
    ...campaign,
    isActive: isCampaignActive(campaign),
    responses,
    respondedCount: responses.length,
    dismissedCount,
    promoters: summary.promoter,
    neutrals: summary.neutral,
    detractors: summary.detractor,
    average: summary.average,
    nps: summary.nps,
    byRole,
  };
};
